import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useLayoutEffect, useRef } from 'react';
import { Box, ButtonBase, Container, Typography } from '@mui/material';
import { useLocation } from 'react-router';
import { styled } from '@mui/material/styles';
import { accentHover, focusOutline } from '../../components/page-primitives';
import { SearchBox } from '../../components/search-box';
import { useSearch } from '../../hooks/use-search';
import { useSearchQuery } from '../../context/search/search-context';
import { useSearchFocus } from '../../context/search/search-focus-context';
import { useSearchBar } from '../../context/search/page-search-bar-context';
import { MONO_FONT } from '../../default/theme';
const PopularChip = styled(ButtonBase)(({ theme }) => ({
    backgroundColor: theme.palette.surface2,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    fontWeight: 400,
    padding: '0.3125rem 0.625rem',
    borderRadius: theme.shape.borderRadiusPill,
    overflow: 'hidden',
    fontFamily: MONO_FONT,
    transition: 'border-color 0.14s, color 0.14s',
    ...accentHover(theme),
    ...focusOutline(theme)
}));
/**
 * The hero search section. Registers itself as the page's search bar while in
 * view (the nav bar hides its own field) and binds to the shared draft query.
 */
export const HeroSearch = ({ searchHeader: SearchHeader, popularSearches = [] }) => {
    const { query } = useSearchQuery();
    const { search } = useSearch();
    const { searchFocusSignal } = useSearchFocus();
    const heroInputRef = useRef(null);
    const isActiveSearchBar = useSearchBar(heroInputRef);
    // Focus the search by default when the hero page is the app's landing page.
    const { key: locationKey } = useLocation();
    useLayoutEffect(() => {
        if (locationKey === 'default') {
            searchFocusSignal.emit();
        }
    }, [locationKey, searchFocusSignal.emit]);
    const handleInputChange = (e) => search({ query: e.target.value }, { debounce: true });
    return (_jsxs(Container, { maxWidth: 'xl', component: 'section', sx: { textAlign: 'center' }, children: [SearchHeader && _jsx(SearchHeader, {}), _jsx(SearchBox, { ref: heroInputRef, value: query, onChange: handleInputChange, onSearch: term => search({ query: term }), viewTransitionName: isActiveSearchBar ? 'vt-search' : undefined }), popularSearches.length > 0 && (_jsxs(Box, { sx: {
                    display: 'flex',
                    gap: '0.5625rem',
                    justifyContent: { xs: 'flex-start', sm: 'center' },
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    overflowX: { xs: 'auto', sm: 'visible' },
                    mt: '0.875rem',
                    mx: { xs: '-1rem', sm: 0 },
                    px: { xs: '1rem', sm: 0 },
                    pb: { xs: '0.25rem', sm: 0 },
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none'
                }, children: [_jsx(Typography, { component: 'span', sx: { fontSize: '0.8125rem', color: 'text.disabled', alignSelf: 'center', flexShrink: 0 }, children: "Popular:" }), popularSearches.map(chip => (_jsx(PopularChip, { onClick: () => search({ query: chip }), style: { flexShrink: 0 }, children: chip }, chip)))] }))] }));
};
//# sourceMappingURL=hero-search.js.map